import { DTypeDTOTypeEnum, MTypeDTOTypeEnum, SteelSMTypeDTOTypeEnum } from '~/api/webservice/index';

import {
  HeightOffsetVariantType,
  HeightOffsetVariantTypes,
  ISOPROConcreteCovering,
  ISOPROHeight,
  ISOPROLengths,
  ISOPROLoadBearingCapacity,
  ISOPROShearForceCapacity,
  ISOPROShearForceCapacityCode,
  ISOPROType,
} from './product.types';

type OptionsRecord<Keys extends string | number, OptionTag extends string> = {
  [K in `${Keys}` | "_none"]: K extends "_none"
  ? "blank"
  : `options.${OptionTag}.${K}`;
};

function toDictionary<T extends string | number, Tag extends string> (
  dict: Record<string, T>,
  tag: Tag
): OptionsRecord<T, Tag> {
  const tags = Object.values(dict).reduce(
    (t, value) => ({ ...t, [value]: `options.${tag}.${value}` }),
    {} as { [K in T]: `options.${Tag}.${K}` }
  );
  return { _none: "blank", ...tags } as OptionsRecord<T, Tag>;
}

/** */
export const TYPE_OPTIONS: OptionsRecord<ISOPROType, "type"> = toDictionary(
  ISOPROType,
  "type"
);

/** */
export const LOAD_BEARING_CAPACITY_OPTIONS: OptionsRecord<
  ISOPROLoadBearingCapacity,
  "loadBearingCapacity"
> = toDictionary(ISOPROLoadBearingCapacity, "loadBearingCapacity");

/** */
export const HEIGHT_OPTIONS: OptionsRecord<ISOPROHeight, "height"> =
  toDictionary(ISOPROHeight, "height");

/** */
export const CONCRETE_COVERING_OPTIONS: OptionsRecord<
  ISOPROConcreteCovering,
  "concreteCovering"
> = {
  _none: "blank",
  [ISOPROConcreteCovering.cv35]: "options.concreteCovering.35",
  [ISOPROConcreteCovering.cv50]: "options.concreteCovering.50",
};

/** */
export const FIRST_SHEAR_LOAD_FORCE: OptionsRecord<
  ISOPROShearForceCapacity<MTypeDTOTypeEnum>["0"],
  "shearForce"
> = {
  _none: "blank",
  4: "options.shearForce.4",
  6: "options.shearForce.6",
  8: "options.shearForce.8",
  10: "options.shearForce.10",
};

/** */
export const SECOND_SHEAR_LOAD_FORCE: OptionsRecord<
  NonNullable<ISOPROShearForceCapacity<MTypeDTOTypeEnum>["1"]>,
  "shearForce"
> = {
  _none: "blank",
  4: "options.shearForce.4",
  6: "options.shearForce.6",
};

/** */
export const SHEAR_LOAD_FORCE: OptionsRecord<
  NonNullable<
    ISOPROShearForceCapacityCode<
      MTypeDTOTypeEnum
      | DTypeDTOTypeEnum
      | SteelSMTypeDTOTypeEnum
    >
  >,
  "shearForce"
> = {
  _none: "blank",
  Q4: "options.shearForce.Q4",
  Q6: "options.shearForce.Q6",
  Q8: "options.shearForce.Q8",
  Q10: "options.shearForce.Q10",
  // Q12: "options.shearForce.Q12",
  Q4Q4: "options.shearForce.Q4Q4",
  Q6Q4: "options.shearForce.Q6Q4",
  Q6Q6: "options.shearForce.Q6Q6",
  Q8Q4: "options.shearForce.Q8Q4",
  QQ: "options.shearForce.QQ"
};

/** */
export const FIRE_PROTECTION_OPTIONS: OptionsRecord<
  "true" | "false",
  "affirmation"
> = {
  _none: "blank",
  true: "options.affirmation.true",
  false: "options.affirmation.false",
};

/** */
export const LENGTH_OPTIONS: OptionsRecord<
  NonNullable<ISOPROLengths>,
  "length"
> = {
  _none: "blank",
  220: "options.length.220",
  300: "options.length.300",
  400: "options.length.400",
  500: "options.length.500",
  1000: "options.length.1000"
};

export const VARIANT_OPTIONS: OptionsRecord<
  NonNullable<HeightOffsetVariantTypes>, "variant"> = toDictionary(HeightOffsetVariantType, "variant");
